import moment from 'moment';
import { FilterStatusEnum } from '../../utils/Enum';

export const candidateSortOptions = {
  previouslyRated: 'previously-rated',
  skillsMatch: 'skills-match',
  localFavorites: 'local-favorites',
};

export const defaultFilters = {
  sort: candidateSortOptions.localFavorites,
  page: 1,
  totalPages: 1,
  limit: 5,
};

export const formatExperienceDates = (
  startDate: string,
  endDate: string | null | undefined,
  isCurrentEmployer: boolean,
): string => {
  const formattedStart = moment(startDate).format('MMMM DD, YYYY');

  if (isCurrentEmployer) {
    return `${formattedStart} / Present`;
  }

  if (endDate) {
    const formattedEnd = moment(endDate).format('MMMM DD, YYYY');
    return `${formattedStart} / ${formattedEnd}`;
  }

  return formattedStart;
};

export const formatGraduationDate = (graduationDate: string | null | undefined): string => {
  if (!graduationDate) {
    return '-';
  }

  return moment(graduationDate).format('MMMM DD, YYYY');
};

/**
 * Route an office lands on after closing the onsite-decline success popup.
 * Migrated (M2M) flow with other candidates still in play returns to the job;
 * everything else returns to the permanent-job overview.
 */
export const getDeclineSuccessRoute = ({
  isEnablePermanentJobM2M,
  hasMultipleCounters,
  jobId,
  postingId,
}: {
  isEnablePermanentJobM2M: boolean;
  hasMultipleCounters: boolean;
  jobId: string | undefined;
  postingId: string | undefined;
}): string =>
  isEnablePermanentJobM2M && hasMultipleCounters
    ? `/dashboard/job/${jobId}`
    : `/permanent-jobs/job-overview/${postingId}`;

interface ICandidatePositionCounter {
  candidate?: { id?: string } | null;
}

export interface ICandidatePosition {
  candidateNumber?: number;
  candidateCount: number;
}

/**
 * Position (1-indexed) and total of a candidate within the counter-offer list.
 *
 * Shared by every confirm_candidate event so `candidate_number` /
 * `candidate_count` are derived identically on the impression
 * (`candidate_viewed`) and on the confirm/decline actions — otherwise the two
 * can't be joined per candidate (GOTU-4816). Returns an undefined
 * `candidateNumber` when the candidate isn't in the list, so a missing position
 * is absent from the payload rather than reported as a bogus 0.
 */
export const getCandidatePosition = (
  allCandidates: ICandidatePositionCounter[] | null | undefined,
  candidateId: string | null | undefined,
): ICandidatePosition => {
  const candidateCount = allCandidates?.length ?? 0;

  // Bail on a missing id rather than matching it: a counter with no candidate would
  // otherwise satisfy `undefined === undefined` and report a bogus position.
  if (!candidateId) {
    return { candidateNumber: undefined, candidateCount };
  }

  const index =
    allCandidates?.findIndex((counter) => counter?.candidate?.id === candidateId) ?? -1;

  return {
    candidateNumber: index >= 0 ? index + 1 : undefined,
    candidateCount,
  };
};

export type TConfirmScreenStatus =
  | 'another_job_popup'
  | 'ready'
  | 'redirect_scheduled'
  | 'redirect_empty'
  | 'decline_success'
  | 'loading';

interface IConfirmScreenStateInput {
  isAnotherJobPopupOpen: boolean;
  isConfirmWorkflowReady: boolean;
  isConfirmJobLoading: boolean;
  hasRenderedConfirmWorkflow: boolean;
  job: { id?: string; status?: string } | null | undefined;
  jobId: string | undefined;
  isSuccessFetchJobCandidates: boolean;
  hasCandidatesAvailable: boolean;
  isOnsiteInterview: boolean;
  isDeclineCandidateSuccess: boolean;
}

/**
 * Resolves the Confirm Candidate screen into a single named outcome so the
 * component can render/redirect by switching on one value instead of re-deriving
 * scattered booleans. Pure: it decides *what* the screen is, never performs the
 * navigation — callers act on the result (intentionally still during render; see
 * the redirect call sites).
 *
 * The `redirect_scheduled` outcome is deliberately suppressed mid-confirm and
 * after the workflow has rendered (GOTU-4020 / GOTU-4025 / GOTU-4027): a confirm
 * invalidates the job query, which refetches the job as SCHEDULED. Without these
 * guards the cold-entry redirect would fire post-confirm and bounce the office to
 * the shift summary before the invite popup shows, clobbering popup-driven
 * navigation. Only a true cold entry (stale SCHEDULED URL) should redirect.
 * TODO(GOTU-4025): remove the guards once confirm navigation is owned by the call
 * site instead of inferred from refetched server state.
 */
export const getConfirmScreenStatus = ({
  isAnotherJobPopupOpen,
  isConfirmWorkflowReady,
  isConfirmJobLoading,
  hasRenderedConfirmWorkflow,
  job,
  jobId,
  isSuccessFetchJobCandidates,
  hasCandidatesAvailable,
  isOnsiteInterview,
  isDeclineCandidateSuccess,
}: IConfirmScreenStateInput): TConfirmScreenStatus => {
  if (isAnotherJobPopupOpen) {
    return 'another_job_popup';
  }

  if (isConfirmWorkflowReady) {
    return 'ready';
  }

  if (
    !isConfirmJobLoading &&
    !hasRenderedConfirmWorkflow &&
    jobId === job?.id &&
    job?.status === FilterStatusEnum.SCHEDULED
  ) {
    return 'redirect_scheduled';
  }

  if (
    job &&
    job.status === FilterStatusEnum.OPEN &&
    isSuccessFetchJobCandidates &&
    !hasCandidatesAvailable &&
    !isOnsiteInterview
  ) {
    return 'redirect_empty';
  }

  if (isDeclineCandidateSuccess && isOnsiteInterview) {
    return 'decline_success';
  }

  return 'loading';
};
